import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import AccessRequests from './AccessRequests';

const PARTNER_COUNTRIES = [
  'Australia', 'Bangladesh', 'Cambodia', 'China', 'Hong Kong', 'India',
  'Indonesia', 'Japan', 'Malaysia', 'Myanmar', 'Nepal', 'New Zealand',
  'Pakistan', 'Philippines', 'Singapore', 'South Korea', 'Sri Lanka',
  'Taiwan', 'Thailand', 'UAE', 'Vietnam', 'Other'
];

const EMPTY_INVITE = {
  email: '', given_name: '', family_name: '',
  partner_company: '', partner_country: '', is_ck_admin: false,
  partner_role: 'partner_admin'
};

export default function UserManagement() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_INVITE);
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    adminService.listUsers()
      .then(data => setUsers(data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    setDeleting(prev => ({ ...prev, [email]: true }));
    try {
      await adminService.deleteUser(email);
      setUsers(prev => prev.filter(u => u.email !== email));
      setMessage({ type: 'success', text: `${email} has been deleted.` });
    } catch {
      setMessage({ type: 'error', text: `Failed to delete ${email}.` });
    } finally {
      setDeleting(prev => ({ ...prev, [email]: false }));
    }
  };

  const handleInvite = async e => {
    e.preventDefault();
    if (!form.email) return;
    setInviting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await adminService.inviteUser(form);
      setMessage({
        type: 'success',
        text: `Invited ${form.email}. Temporary password: ${res.temp_password}`
      });
      setForm(EMPTY_INVITE);
      // Refresh list
      adminService.listUsers().then(data => setUsers(data.users || []));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to invite user' });
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="user-management">
      <div className="um-header">
        <h2>User Management</h2>
        <div className="um-tabs">
          <button className={`um-tab${tab === 'users' ? ' active' : ''}`} onClick={() => setTab('users')}>Users</button>
          <button className={`um-tab${tab === 'requests' ? ' active' : ''}`} onClick={() => setTab('requests')}>Access Requests</button>
        </div>
      </div>

      {tab === 'requests' && <AccessRequests />}
      {tab === 'users' && <>

      {/* Invite form */}
      <div className="invite-panel">
        <h3>Invite New User</h3>
        {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
        <form onSubmit={handleInvite}>
          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="partner@company.com" />
            </div>
            <div className="form-group">
              <label>First Name</label>
              <input name="given_name" value={form.given_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input name="family_name" value={form.family_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Partner Company</label>
              <input name="partner_company" value={form.partner_company} onChange={handleChange} placeholder="Company name" />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select name="partner_country" value={form.partner_country} onChange={handleChange}>
                <option value="">Select…</option>
                {PARTNER_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Partner Role</label>
              <select name="partner_role" value={form.partner_role} onChange={handleChange} disabled={form.is_ck_admin}>
                <option value="partner_admin">Partner Admin (full access)</option>
                <option value="manager">Manager (can approve deals)</option>
                <option value="salesperson">Salesperson (submit deals)</option>
              </select>
              <small style={{ color: '#6B7280', marginTop: '0.25rem', display: 'block' }}>
                Role determines permissions within their partner company
              </small>
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input type="checkbox" name="is_ck_admin" checked={form.is_ck_admin} onChange={handleChange} />
              CK Admin (can see all opportunities, dashboards, invite users)
            </label>
          </div>
          <button type="submit" className="btn-primary" disabled={inviting}>
            {inviting ? 'Inviting…' : 'Send Invite'}
          </button>
        </form>
      </div>

      {/* User table */}
      <div className="users-panel">
        <h3>All Users ({users.length})</h3>
        {loading ? <div className="loading">Loading…</div> : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Country</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.username}>
                    <td>{u.email}</td>
                    <td>{[u.given_name, u.family_name].filter(Boolean).join(' ') || '—'}</td>
                    <td>{u.company || '—'}</td>
                    <td>{u.country || '—'}</td>
                    <td>
                      <span className="tag" style={{ fontSize: '0.75rem' }}>
                        {u.role === 'manager' ? 'Manager' : u.role === 'salesperson' ? 'Salesperson' : 'Partner Admin'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${u.status === 'CONFIRMED' ? 'synced' : 'pending'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <button
                        className="delete-user-btn"
                        onClick={() => handleDelete(u.email)}
                        disabled={deleting[u.email]}
                      >
                        {deleting[u.email] ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}
    </div>
  );
}
